import type { SlaSummary } from '@nest-recruit/shared';

/**
 * The two SLA figures in words (master plan S9, L7): "14 days elapsed, 9 days active".
 *
 * Elapsed is calendar time and reads in days. Active is working time and reads in
 * working days of the default calendar (09:45–18:45, nine hours) — a clock that has
 * run one night shows "2.7 days elapsed · 2h active", which is QA test 3 exactly.
 */

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
/** D14's default working day. A per-department calendar differs; the figure is a reading aid. */
const WORKING_MINUTES_PER_DAY = 9 * MINUTES_PER_HOUR;

export function formatElapsed(minutes: number): string {
  if (minutes < MINUTES_PER_HOUR) return `${minutes} min`;
  if (minutes < MINUTES_PER_DAY) return `${Math.floor(minutes / MINUTES_PER_HOUR)}h`;
  const days = Math.round((minutes / MINUTES_PER_DAY) * 10) / 10;
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

export function formatActive(minutes: number): string {
  if (minutes < MINUTES_PER_HOUR) return `${minutes} min`;
  if (minutes < WORKING_MINUTES_PER_DAY) {
    const hours = Math.floor(minutes / MINUTES_PER_HOUR);
    const rest = minutes % MINUTES_PER_HOUR;
    return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
  }
  const days = Math.round((minutes / WORKING_MINUTES_PER_DAY) * 10) / 10;
  return `${days} working ${days === 1 ? 'day' : 'days'}`;
}

/** "2.7 days elapsed · 2h active" — the pair every SLA display shows. */
export function slaFigures(sla: Pick<SlaSummary, 'elapsedMinutes' | 'activeMinutes'>): string {
  return `${formatElapsed(sla.elapsedMinutes)} elapsed · ${formatActive(sla.activeMinutes)} active`;
}

/** "of a 3 working day limit", or nothing when the clock has no limit. */
export function slaLimit(sla: Pick<SlaSummary, 'targetWorkingMinutes'>): string | null {
  if (sla.targetWorkingMinutes === null) return null;
  return `limit ${formatActive(sla.targetWorkingMinutes)}`;
}
